import React, { useCallback, useEffect, useState } from 'react';
import FieldRow from './FieldRow';
import DocCanvas from './DocCanvas';
import AmountsTable from './AmountsTable';
import ArcaBadge from './ArcaBadge';
import ArcaDetailsModal from './ArcaDetailsModal';
import ArcaChip from './ArcaChip';
import ClassifyCard from './ClassifyCard';
import ItemsSection from './ItemsSection';
import JsonSheet from './JsonSheet';
import { formatArs } from '../../utils/documentFichaMapper';
import { formatVoucherTitle } from '../../utils/fiscalComprobante';
import {
  constatarDocumentArca,
  fetchArcaValidationAudit,
  patchDocumentField,
  fetchDocumentFicha,
  getPresignedUrl,
} from '../../common/apiClient';
import './DocumentView.css';

function folderKey(s3Key) {
  const idx = (s3Key || '').lastIndexOf('/');
  return idx === -1 ? s3Key : s3Key.substring(0, idx);
}

function Section({ title, children }) {
  return (
    <section className="dv-section">
      <h3 className="dv-section-title">{title}</h3>
      <div className="dv-section-body">{children}</div>
    </section>
  );
}

export default function DocumentView({
  ficha: initialFicha,
  onClose,
  onClassify,
  reprocessDocTypes = [],
  onFichaChange,
  arcaEnabled = false,
  canConstatar = false,
  reprocessable = false,
  onReprocess,
  reprocessLoading = false,
  isReprocessing = false,
}) {
  const [ficha, setFicha] = useState(initialFicha);
  const [mobilePanel, setMobilePanel] = useState('datos');
  const [highlightKey, setHighlightKey] = useState(null);
  const [jsonOpen, setJsonOpen] = useState(false);
  const [arcaLoading, setArcaLoading] = useState(false);
  const [arcaDetailsOpen, setArcaDetailsOpen] = useState(false);
  const [arcaDetailsLoading, setArcaDetailsLoading] = useState(false);
  const [arcaAudit, setArcaAudit] = useState(null);
  const [toast, setToast] = useState(null);
  const [classifyLoading, setClassifyLoading] = useState(false);

  useEffect(() => {
    setFicha((prev) => {
      if (!initialFicha) return initialFicha;
      if (!prev || prev.doc_id !== initialFicha.doc_id) return initialFicha;
      return initialFicha;
    });
    setHighlightKey(null);
  }, [initialFicha]);

  const reducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;

  const patchFicha = useCallback(
    (next) => {
      setFicha(next);
      onFichaChange?.(next);
    },
    [onFichaChange]
  );

  const handleHighlight = useCallback((fieldKey) => {
    setHighlightKey(fieldKey);
    if (window.innerWidth < 960) {
      setMobilePanel('documento');
    }
  }, []);

  const handleSaveField = async (fieldKey, value) => {
    if (!ficha?.permissions?.can_edit) return;
    try {
      if (!ficha.doc_id) {
        const field = ficha.fields[fieldKey];
        patchFicha({
          ...ficha,
          fields: {
            ...ficha.fields,
            [fieldKey]: {
              ...field,
              value,
              edited: true,
              original_value: field?.original_value ?? field?.value,
            },
          },
        });
        setToast('Guardado ✓');
        setTimeout(() => setToast(null), 2500);
        return;
      }

      const res = await patchDocumentField(ficha.doc_id, fieldKey, value);
      const fieldMeta = res?.field || {};
      const prevField = ficha.fields[fieldKey] || {};
      const nextField = {
        ...prevField,
        ...fieldMeta,
        value: fieldMeta.value ?? value,
        edited: true,
        original_value:
          fieldMeta.original_value ?? prevField.original_value ?? prevField.value,
        band: fieldMeta.band ?? prevField.band,
      };
      const nextFields = { ...ficha.fields, [fieldKey]: nextField };
      patchFicha({
        ...ficha,
        fields: nextFields,
        amounts: res?.amounts || ficha.amounts,
        fiscal: {
          ...ficha.fiscal,
          ...(fieldKey === 'fiscal.cae' ? { cae: nextField.value } : {}),
          ...(fieldKey === 'fiscal.cae_due' ? { cae_due: nextField.value } : {}),
        },
        parties: {
          issuer: {
            ...ficha.parties?.issuer,
            ...(fieldKey === 'parties.issuer.name'
              ? { name: nextField.value }
              : {}),
            ...(fieldKey === 'parties.issuer.cuit'
              ? { cuit: nextField.value }
              : {}),
          },
          receiver: {
            ...ficha.parties?.receiver,
            ...(fieldKey === 'parties.receiver.name'
              ? { name: nextField.value }
              : {}),
            ...(fieldKey === 'parties.receiver.cuit'
              ? { cuit: nextField.value }
              : {}),
          },
        },
        voucher: {
          ...ficha.voucher,
          ...(fieldKey === 'voucher.full_number'
            ? { full_number: nextField.value }
            : {}),
          ...(fieldKey === 'voucher.issue_date'
            ? { issue_date: nextField.value }
            : {}),
        },
      });
      setToast('Guardado ✓');
      setTimeout(() => setToast(null), 2500);
    } catch (err) {
      setToast(err.message || 'Error al guardar');
    }
  };

  const handleConstatar = async () => {
    setArcaLoading(true);
    try {
      const useV1 = process.env.REACT_APP_DOCUMENT_V1_ENABLED === 'true';
      if (ficha.doc_id) {
        const result = await constatarDocumentArca(ficha.doc_id);
        const updated = await fetchDocumentFicha(ficha.doc_id);
        if (useV1 && updated) patchFicha(updated);
        else if (result?.arca_validation) {
          patchFicha({
            ...ficha,
            arca: {
              ...result.arca_validation,
              state: result.arca_validation.status,
              observaciones: result.arca_validation.observations || [],
              comparison: result.arca_validation.comparison || [],
            },
          });
        }
        if (result?.audit?.[0]) {
          setArcaAudit(result.audit[0]);
        }
      }
    } catch (err) {
      setToast(err.message || 'Error al constatar');
    } finally {
      setArcaLoading(false);
    }
  };

  const handleShowArcaDetails = async () => {
    setArcaDetailsOpen(true);
    if (ficha.arca?.comparison?.length) {
      return;
    }
    if (!ficha.doc_id) return;
    setArcaDetailsLoading(true);
    try {
      const audits = await fetchArcaValidationAudit(ficha.doc_id, 1);
      const latest = audits?.[0];
      if (latest) {
        setArcaAudit(latest);
        if (latest.comparison?.length) {
          patchFicha({
            ...ficha,
            arca: {
              ...ficha.arca,
              comparison: latest.comparison,
            },
          });
        }
      }
    } catch (err) {
      setToast(err.message || 'No se pudo cargar el detalle ARCA');
    } finally {
      setArcaDetailsLoading(false);
    }
  };

  const handleClassify = async (targetType) => {
    setClassifyLoading(true);
    try {
      await onClassify?.(targetType);
    } finally {
      setClassifyLoading(false);
    }
  };

  const toggleMobilePane = () => {
    setMobilePanel((p) => (p === 'datos' ? 'documento' : 'datos'));
  };

  const handleDownload = async () => {
    const s3Key = ficha?.document?.key;
    if (!s3Key) {
      setToast('No hay archivo para descargar');
      return;
    }
    try {
      const presigned = await getPresignedUrl({
        key: folderKey(s3Key),
        contentType: ficha.document?.mimetype || 'application/pdf',
        presigned_type: 'get_object',
        file_name: ficha.filename,
      });
      if (!presigned?.url) {
        throw new Error('No se pudo obtener URL de descarga');
      }
      const a = document.createElement('a');
      a.href = presigned.url;
      a.download = ficha.filename || 'documento';
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      a.click();
    } catch (err) {
      setToast(err?.message || 'Error al descargar');
    }
  };

  if (!ficha) return null;

  const fields = ficha.fields || {};
  const activeBbox = highlightKey ? fields[highlightKey]?.bbox : null;
  const issuer = ficha.parties?.issuer || {};
  const receiver = ficha.parties?.receiver || {};
  const voucher = ficha.voucher || {};
  const isUnclassified = ficha.status === 'unclassified';
  const isUsd = ficha.amounts?.currency === 'USD';

  const bedrock = ficha.raw?.bedrock_json || {};
  const titleParts = [
    ficha.voucher_title || formatVoucherTitle(bedrock, ficha.letter),
    voucher.full_number,
  ].filter(Boolean);
  const title = titleParts.join(' · ') || ficha.filename;
  const subtitle = [
    issuer.name,
    receiver.name && `→ ${receiver.name}`,
    voucher.issue_date && `· emitida ${voucher.issue_date}`,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="dv-shell">
      {isReprocessing && (
        <div className="dv-reprocess-banner" role="status">
          <span className="dv-reprocess-spinner" aria-hidden="true" />
          Reprocesando documento… extrayendo datos del archivo
        </div>
      )}
      <header className="dv-modal-head">
        <div className="dv-mh-top">
          <div className="dv-mh-title">
            <h1>{title}</h1>
            {subtitle && <div className="dv-mh-sub">{subtitle}</div>}
          </div>
          <div className="dv-mh-right">
            <button type="button" className="dv-close" onClick={onClose} aria-label="Cerrar">
              ✕
            </button>
            <div className={`dv-mh-total num${isUsd ? ' dv-mh-total--usd' : ''}${reducedMotion ? '' : ' dv-total-animate'}`}>
              {formatArs(ficha.amounts?.total)}
            </div>
          </div>
        </div>
        <div className="dv-mh-bar">
          {arcaEnabled && <ArcaChip arca={ficha.arca} loading={arcaLoading} />}
          {arcaEnabled && (
            <button
              type="button"
              className="dv-btn-primary"
              disabled={arcaLoading}
              onClick={handleConstatar}
            >
              {arcaLoading ? 'Validando ARCA…' : 'Constatar ARCA'}
            </button>
          )}
          {reprocessable && onReprocess && (
            <button
              type="button"
              className="dv-btn"
              disabled={reprocessLoading}
              onClick={onReprocess}
            >
              {reprocessLoading ? 'Reprocesando…' : '↻ Reprocesar'}
            </button>
          )}
          <span className="dv-mh-spacer" />
          <button
            type="button"
            className="dv-btn dv-pane-toggle"
            onClick={toggleMobilePane}
          >
            {mobilePanel === 'datos' ? 'Ver documento' : 'Ver datos'}
          </button>
          <button
            type="button"
            className={`dv-btn${jsonOpen ? ' dv-btn--on' : ''}`}
            onClick={() => setJsonOpen(true)}
          >
            ⟨/⟩ JSON
          </button>
          <button
            type="button"
            className="dv-btn"
            aria-label="Descargar"
            onClick={handleDownload}
            disabled={!ficha.document?.key}
          >
            ↓ Descargar
          </button>
        </div>
      </header>

      {jsonOpen ? (
        <JsonSheet
          open
          data={ficha.raw?.bedrock_json || {}}
          filename={ficha.filename}
          onClose={() => setJsonOpen(false)}
        />
      ) : (
      <div className="dv-split">
        <div
          className={`dv-pane-data${mobilePanel === 'datos' ? ' dv-pane--visible' : ''}${reprocessLoading ? ' dv-pane--busy' : ''}`}
        >
          {isUnclassified && (
            <ClassifyCard
              hypothesis={ficha.classifier_hypothesis}
              aiSummary={ficha.ai_summary}
              docTypes={reprocessDocTypes}
              loading={classifyLoading}
              onConfirm={handleClassify}
              onChooseOther={handleClassify}
            />
          )}

          <Section title="📄 Comprobante">
            <FieldRow
              fieldKey="voucher.full_number"
              label="N° comprobante"
              value={fields['voucher.full_number']?.value}
              mono
              {...fields['voucher.full_number']}
              canEdit={ficha.permissions?.can_edit}
              active={highlightKey === 'voucher.full_number'}
              onHighlight={handleHighlight}
              onSave={handleSaveField}
            />
            <FieldRow
              fieldKey="voucher.issue_date"
              label="Fecha emisión"
              value={fields['voucher.issue_date']?.value}
              {...fields['voucher.issue_date']}
              canEdit={ficha.permissions?.can_edit}
              active={highlightKey === 'voucher.issue_date'}
              onHighlight={handleHighlight}
              onSave={handleSaveField}
            />
            {voucher.due_date && (
              <FieldRow
                fieldKey="voucher.due_date"
                label="Vencimiento"
                value={voucher.due_date}
                band="high"
                confidence={0.9}
                onHighlight={handleHighlight}
              />
            )}
            {voucher.payment_terms && (
              <FieldRow
                fieldKey="voucher.payment_terms"
                label="Condición pago"
                value={voucher.payment_terms}
                band="high"
                confidence={0.9}
                onHighlight={handleHighlight}
              />
            )}
          </Section>

          <Section title="🏢 Partes">
            {(ficha.fiscal_enrichment?.warnings || []).includes('emisor_brand_suspected') && (
              <div className="dv-arca-obs dv-arca-obs--observado">
                El nombre del emisor podría ser una marca comercial. Verificá el CUIT en el pie fiscal antes de constatar ARCA.
              </div>
            )}
            {(ficha.fiscal_enrichment?.warnings || []).includes('cuit_emisor_low_confidence_no_qr') && (
              <div className="dv-arca-obs dv-arca-obs--observado">
                El CUIT emisor tiene baja confianza y no hay QR AFIP para validarlo. Revisá el dato antes de constatar ARCA.
              </div>
            )}
            <FieldRow
              fieldKey="parties.issuer.name"
              label="Emisor"
              value={fields['parties.issuer.name']?.value}
              {...fields['parties.issuer.name']}
              canEdit={ficha.permissions?.can_edit}
              active={highlightKey === 'parties.issuer.name'}
              onHighlight={handleHighlight}
              onSave={handleSaveField}
            />
            <FieldRow
              fieldKey="parties.issuer.cuit"
              label="CUIT emisor"
              value={fields['parties.issuer.cuit']?.value}
              mono
              {...fields['parties.issuer.cuit']}
              canEdit={ficha.permissions?.can_edit}
              active={highlightKey === 'parties.issuer.cuit'}
              onHighlight={handleHighlight}
              onSave={handleSaveField}
            />
            <FieldRow
              fieldKey="parties.receiver.name"
              label="Receptor"
              value={fields['parties.receiver.name']?.value}
              {...fields['parties.receiver.name']}
              canEdit={ficha.permissions?.can_edit}
              active={highlightKey === 'parties.receiver.name'}
              onHighlight={handleHighlight}
              onSave={handleSaveField}
            />
            <FieldRow
              fieldKey="parties.receiver.cuit"
              label="CUIT receptor"
              value={fields['parties.receiver.cuit']?.value}
              mono
              {...fields['parties.receiver.cuit']}
              canEdit={ficha.permissions?.can_edit}
              active={highlightKey === 'parties.receiver.cuit'}
              onHighlight={handleHighlight}
              onSave={handleSaveField}
            />
          </Section>

          <AmountsTable
            amounts={ficha.amounts}
            highlightKey={highlightKey}
            onHighlight={handleHighlight}
          />

          <ItemsSection items={ficha.items} />

          {arcaEnabled && (
            <Section title="🛡️ Validación ARCA">
              <ArcaBadge
                arca={ficha.arca}
                fiscal={ficha.fiscal}
                fiscalEnrichment={ficha.fiscal_enrichment}
                loading={arcaLoading}
                onConstatar={arcaEnabled ? handleConstatar : undefined}
                onShowDetails={handleShowArcaDetails}
              />
              <FieldRow
                fieldKey="fiscal.cae"
                label="CAE"
                value={fields['fiscal.cae']?.value}
                mono
                {...fields['fiscal.cae']}
                canEdit={ficha.permissions?.can_edit}
                active={highlightKey === 'fiscal.cae'}
                onHighlight={handleHighlight}
                onSave={handleSaveField}
              />
              <FieldRow
                fieldKey="fiscal.cae_due"
                label="Vencimiento CAE"
                value={fields['fiscal.cae_due']?.value}
                {...fields['fiscal.cae_due']}
                canEdit={ficha.permissions?.can_edit}
                active={highlightKey === 'fiscal.cae_due'}
                onHighlight={handleHighlight}
                onSave={handleSaveField}
              />
            </Section>
          )}

          {ficha.accounting && (
            <Section title="📊 Imputación contable">
              <FieldRow
                fieldKey="accounting.category"
                label="Categoría"
                value={ficha.accounting.category || '—'}
                band="medium"
                confidence={0.6}
                canEdit={ficha.permissions?.can_edit}
                onHighlight={handleHighlight}
              />
            </Section>
          )}

          <details className="dv-section dv-section--collapsible dv-section--summary">
            <summary className="dv-section-title">✨ Resumen IA</summary>
            <div className="dv-section-body">
              <p className="dv-summary-text">
                {ficha.ai_summary ||
                  'No hay resumen generado para este comprobante. Podés revisar los datos extraídos e ítems del documento.'}
              </p>
            </div>
          </details>
        </div>

        <div
          className={`dv-pane-doc${mobilePanel === 'documento' ? ' dv-pane--visible' : ''}`}
        >
          <DocCanvas
            s3Key={ficha.document?.key}
            mimetype={ficha.document?.mimetype}
            filename={ficha.filename}
            activeBbox={activeBbox}
            onRegionClick={() => highlightKey && setMobilePanel('datos')}
          />
        </div>
      </div>
      )}

      <div className={`dv-toast${toast ? ' dv-toast--show' : ''}`}>{toast}</div>

      <ArcaDetailsModal
        open={arcaDetailsOpen}
        onClose={() => setArcaDetailsOpen(false)}
        arca={ficha.arca}
        audit={arcaAudit}
        loading={arcaDetailsLoading}
      />
    </div>
  );
}
